import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Calendar, 
  CheckCircle,
  FileText, 
  Mic, 
  Brain, 
  Trash2, 
  BookOpen, 
  Users, 
  Target,
  HelpCircle,
  Edit3,
  GraduationCap,
  Zap,
  Clock,
  Loader,
  AlertCircle,
  XCircle,
  Wand2,
  Upload,
  Sparkles,
  RefreshCw,
  X
} from 'lucide-react';
import { Course, StudySession, coursesApi, studySessionsApi } from '../services/api';
import SessionStatusBadge from './SessionStatusBadge';
import { analytics } from '../services/analytics';
import { useTranslation } from 'react-i18next';
import { MixedText } from '../utils/rtl';
import { Exam } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';

const CourseView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const courseId = parseInt(id || '0');

  const [course, setCourse] = useState<Course | null>(null);
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // View State
  const [activeTab, setActiveTab] = useState<'sessions' | 'exams'>('sessions');
  
  // Edit course state
  const [editingCourse, setEditingCourse] = useState(false);
  const [editCourseName, setEditCourseName] = useState('');
  const [editCourseDescription, setEditCourseDescription] = useState('');
  
  // Edit session state
  const [editingSession, setEditingSession] = useState<StudySession | null>(null);
  const [editSessionTitle, setEditSessionTitle] = useState('');
  
  // Exam state
  const [exams, setExams] = useState<Exam[]>([]);
  const [examLoading, setExamLoading] = useState(false);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [deletingExam, setDeletingExam] = useState<number | null>(null);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [editExamName, setEditExamName] = useState('');
  const [showCreationModal, setShowCreationModal] = useState(false);

  useEffect(() => {
    if (courseId) {
      loadCourse();
      loadSessions();
      loadExams();
      // Track course viewed
      analytics.track('Course Viewed', { course_id: courseId });
    }
  }, [courseId]);

  // Periodic status updates for exams
  useEffect(() => {
    if (!courseId || activeTab !== 'exams') return;

    // Only set up periodic refresh if there are exams that might need updates
    const hasProcessingExams = exams.some(exam => 
      exam.processing_status === 'pending' || 
      exam.processing_status === 'processing'
    );

    if (!hasProcessingExams) return;

    const interval = setInterval(async () => {
      await refreshExamStatuses();
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [courseId, exams, activeTab]);

  const refreshExamStatuses = async () => {
    try {
      setRefreshingStatuses(true);
      const updatedExams = await examApi.getCourseExams(courseId);
      
      // Only update if there are actual changes to avoid unnecessary re-renders
      const hasChanges = updatedExams.some((updatedExam, index) => {
        const currentExam = exams[index];
        return !currentExam || 
               currentExam.processing_status !== updatedExam.processing_status ||
               currentExam.id !== updatedExam.id;
      });

      if (hasChanges || updatedExams.length !== exams.length) {
        setExams(updatedExams);
      }
    } catch (err) {
      console.error('Error refreshing exam statuses:', err);
    } finally {
      setRefreshingStatuses(false);
    }
  };

  const loadCourse = async () => {
    try {
      const courseData = await coursesApi.getCourse(courseId);
      setCourse(courseData);
    } catch (err) {
      setError('Failed to load course');
      console.error('Error loading course:', err);
    }
  };

  const loadSessions = async () => {
    try {
      setLoading(true);
      const sessionsData = await coursesApi.getCourseSessions(courseId);
      setSessions(sessionsData);
    } catch (err) {
      setError('Failed to load sessions');
      console.error('Error loading sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadExams = async () => {
    try {
      setExamLoading(true);
      const examsData = await examApi.getCourseExams(courseId);
      setExams(examsData);
    } catch (err) {
      console.error('Error loading exams:', err);
    } finally {
      setExamLoading(false);
    }
  };

  const handleEditCourse = () => {
    if (!course) return;
    setEditingCourse(true);
    setEditCourseName(course.name);
    setEditCourseDescription(course.description || '');
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!course || !editCourseName.trim()) return;

    try {
      const updatedCourse = await coursesApi.updateCourse(course.id, {
        name: editCourseName.trim(),
        description: editCourseDescription.trim() || undefined
      });
      
      analytics.track('Course Updated', {
        course_id: course.id,
        old_name: course.name,
        new_name: editCourseName.trim(),
      });
      
      setCourse(updatedCourse);
      setEditingCourse(false);
      setEditCourseName('');
      setEditCourseDescription('');
    } catch (err) {
      analytics.trackError('Course Update Failed', { 
        courseId: course.id,
        courseName: editCourseName.trim() 
      });
      setError('Failed to update course');
      console.error(err);
    }
  };

  const handleEditSession = (session: StudySession) => {
    setEditingSession(session);
    setEditSessionTitle(session.title);
  };

  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSession || !editSessionTitle.trim()) return;

    try {
      await studySessionsApi.updateSession(editingSession.id, {
        title: editSessionTitle.trim()
      });
      
      analytics.track('Study Session Updated', {
        session_id: editingSession.id,
        course_id: courseId,
      });
      
      setSessions(sessions.map(s => 
        s.id === editingSession.id 
          ? { ...s, title: editSessionTitle.trim() }
          : s
      ));
      
      setEditingSession(null);
      setEditSessionTitle('');
    } catch (err) {
      analytics.trackError('Session Update Failed', { 
        sessionId: editingSession.id,
      });
      setError('Failed to update session');
      console.error('Error updating session:', err);
    }
  };

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm(t('dashboard:study_session.delete_confirm'))) {
      return;
    }

    try {
      await studySessionsApi.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      
      analytics.track('Study Session Deleted', {
        session_id: sessionId,
        course_id: courseId
      });
    } catch (err) {
      setError('Failed to delete session');
      console.error(err);
    }
  };

  // Exam handlers
  const handleDeleteExam = async (examId: number, examTitle: string) => {
    if (!window.confirm(t('exams:examList.confirmDelete'))) {
      return;
    }

    setDeletingExam(examId);
    try {
      await examApi.deleteExam(examId);
      setExams(prev => prev.filter(e => e.id !== examId));
      analytics.track('Exam Deleted', {
        course_id: courseId,
        exam_id: examId,
        exam_title: examTitle
      });
    } catch (err) {
      console.error('Error deleting exam:', err);
      setError(t('common:errors.delete_failed'));
    } finally {
      setDeletingExam(null);
    }
  };

  const handleEditExam = (exam: Exam) => {
    setEditingExam(exam);
    setEditExamName(exam.title);
  };

  const handleUpdateExam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingExam || !editExamName.trim()) return;

    try {
      const updatedExam = await examApi.updateExam(editingExam.id, {
        title: editExamName.trim(),
      });
      
      analytics.track('Exam Updated', {
        exam_id: editingExam.id,
        new_title: editExamName.trim(),
      });
      
      setExams(prev => prev.map(exam => 
        exam.id === editingExam.id ? updatedExam : exam
      ));
      
      setEditingExam(null);
      setEditExamName('');
    } catch (err) {
      analytics.trackError('Exam Update Failed', { 
        examId: editingExam.id,
      });
      setError(t('common:errors.update_failed'));
      console.error(err);
    }
  };

  const handleCreateExamClick = () => {
    const examsWithFiles = exams.filter(exam => exam.s3_uri);
    if (examsWithFiles.length > 0) {
      setShowCreationModal(true);
    } else {
      navigate(`/exams/create?course_id=${courseId}`);
    }
  };

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(activeTab === 'sessions' ? { hour: '2-digit', minute: '2-digit' } : {})
    });
  };

  const getSessionStatus = (session: StudySession): 'not_started' | 'in_progress' | 'completed' => {
    if (session.completed_at) return 'completed';
    const hasContent = (session.transcriptions?.length || 0) > 0 || 
                      (session.documents?.length || 0) > 0 ||
                      (session.study_materials?.length || 0) > 0 ||
                      (session.questions?.length || 0) > 0;
    return hasContent ? 'in_progress' : 'not_started';
  };

  const calculateSessionProgress = (session: StudySession): number => {
    let progress = 0;
    if ((session.transcriptions?.length || 0) > 0 || (session.documents?.length || 0) > 0) progress += 25;
    if ((session.study_materials?.length || 0) > 0) progress += 25;
    if ((session.questions?.length || 0) > 0) progress += 25;
    if (session.completed_at) progress += 25;
    return progress;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      case 'processing': return <Loader className="w-4 h-4 mr-2 ml-2 text-muted-purple animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      case 'failed': return <XCircle className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      default: return <AlertCircle className="w-4 h-4 mr-2 ml-2 text-surface-muted" />;
    }
  };

  const getStatusText = (status: string) => {
    return t(`exams:processing.${status}`);
  };

  const totalSessions = sessions.length;
  const totalExams = exams.length;

  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (!course) {
    return (
      <DashboardContainer>
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold text-surface-primary mb-2">🌌 Course Not Found</h3>
          <p className="text-surface-secondary mb-6">The course you're looking for doesn't exist in our galaxy.</p>
          <Button onClick={() => navigate('/')} variant="primary">
             Back to Dashboard
          </Button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="space-y-4 md:space-y-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
      >
        {isRTL ? (
          <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
        ) : (
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
        )}
        <span>{t('common:actions.back_to_dashboard')}</span>
      </button>

      {error && (
        <Card className="border-vibrant-orange/30 bg-vibrant-orange/10 backdrop-blur-sm">
          <p className="text-vibrant-orange font-medium">⚠️ {error}</p>
        </Card>
      )}

      {/* Course Header */}
      <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
        <CardHeader>
          {/* Mobile Layout - Stacked */}
          <div className="block md:hidden">
            <div className="flex items-center justify-between mb-3">
              <CardTitle className={`text-xl ${isRTL ? 'text-right' : ''} flex-1 mr-2`}>
                <MixedText>{course.name}</MixedText>
              </CardTitle>
              <button
                onClick={handleEditCourse}
                className="p-2 rounded-lg text-surface-secondary hover:text-vibrant-orange hover:bg-vibrant-orange/10 transition-all duration-300 flex-shrink-0"
                title={t('common:actions.edit_course')}
              >
                <Edit3 className="w-4 h-4" />
              </button>
            </div>
            {course.description && (
              <CardContent className={`text-sm mb-3 ${isRTL ? 'text-right' : ''}`}>
                {course.description}
              </CardContent>
            )}
            <div className={`flex items-center text-xs text-surface-muted ${isRTL ? 'justify-end' : ''}`}>
              <span className={`flex items-center font-medium font-hebrew ${isRTL ? 'flex-row-reverse ml-3' : 'mr-3'}`}>
                <Users className={`w-3 h-3 ${isRTL ? 'mr-1 ml-1' : 'mr-1'}`} />
                {totalSessions} {totalSessions !== 1 ? t('common:labels.sessions') : t('common:labels.session')}
              </span>
            </div>
          </div>

          {/* Desktop Layout - Horizontal */}
          <div className="hidden md:block">
            <div className={`flex items-start ${isRTL ? 'justify-start' : 'justify-between'}`}>
              <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'} flex-1`}>
                <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                  <BookOpen className="w-8 h-8 text-vibrant-orange" />
                </div>
                <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-2' : 'order-2'}`}>
                  <CardTitle className={`text-2xl md:text-3xl mb-2 ${isRTL ? 'text-right' : ''}`}>
                    {course.name}
                  </CardTitle>
                  {course.description && (
                    <CardContent className={`text-base md:text-lg mb-3 ${isRTL ? 'text-right' : ''}`}>
                      {course.description}
                    </CardContent>
                  )}
                  <div className={`flex items-center text-sm text-surface-muted ${isRTL ? 'justify-end' : ''}`}>
                    <span className={`flex items-center font-medium font-hebrew ${isRTL ? 'flex-row-reverse ml-4' : 'mr-4'}`}>
                      <Users className={`w-4 h-4 ${isRTL ? 'mr-2 ml-1' : 'mr-1'}`} />
                      {totalSessions} {totalSessions !== 1 ? t('common:labels.sessions') : t('common:labels.session')}
                    </span>
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Calendar className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                      {t('common:labels.created')} {formatDate(course.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2 order-first mr-4' : 'space-x-2'}`}>
                <button
                  onClick={handleEditCourse}
                  className="p-3 md:p-2 rounded-lg text-surface-secondary hover:text-vibrant-orange hover:bg-vibrant-orange/10 transition-all duration-300"
                  title={t('common:actions.edit_course')}
                >
                  <Edit3 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
      </SpaceCard>

      {/* Slider Toggle */}
      <div className="w-full max-w-md mx-auto mb-8">
        <div className="relative flex bg-[#1a0f2b]/60 backdrop-blur-md border border-white/10 rounded-full p-1.5 h-16 md:h-20 shadow-lg">
          {/* Animated Background */}
          <div
            className="absolute top-1.5 bottom-1.5 w-[calc(50%-0.75rem)] rounded-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 shadow-[0_0_20px_rgba(249,115,22,0.4)] transition-all duration-300 ease-out z-0"
            style={{
              left: isRTL 
                ? (activeTab === 'sessions' ? '0.375rem' : 'calc(50% + 0.375rem)')
                : (activeTab === 'sessions' ? 'calc(50% + 0.375rem)' : '0.375rem'),
            }}
          />

          {/* Sessions Tab Button */}
          <button
            onClick={() => setActiveTab('sessions')}
            className={`flex-1 flex items-center justify-center relative z-10 transition-all duration-300 rounded-full ${
              activeTab === 'sessions' ? 'text-white scale-105' : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            <div className={`flex items-center gap-2 md:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img 
                src="/astronaut_reading.png" 
                alt="Sessions" 
                className={`w-6 h-6 md:w-8 md:h-8 transition-all duration-300 ${activeTab === 'sessions' ? 'filter brightness-110 drop-shadow-md' : 'filter grayscale opacity-60'}`}
              />
              <span className={`text-sm md:text-lg font-bold font-hebrew transition-all duration-300 ${activeTab === 'sessions' ? 'text-shadow-sm' : ''}`}>
                <MixedText>{t('dashboard:course_card.course_lessons')}</MixedText>
              </span>
            </div>
          </button>

          {/* Exams Tab Button */}
          <button
            onClick={() => setActiveTab('exams')}
            className={`flex-1 flex items-center justify-center relative z-10 transition-all duration-300 rounded-full ${
              activeTab === 'exams' ? 'text-white scale-105' : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            <div className={`flex items-center gap-2 md:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img 
                src="/astronaut_checkbox.png" 
                alt="Exams" 
                className={`w-6 h-6 md:w-8 md:h-8 transition-all duration-300 ${activeTab === 'exams' ? 'filter brightness-110 drop-shadow-md' : 'filter grayscale opacity-60'}`}
              />
              <span className={`text-sm md:text-lg font-bold font-hebrew transition-all duration-300 ${activeTab === 'exams' ? 'text-shadow-sm' : ''}`}>
                <MixedText>{t('exams:examPractice.title')}</MixedText>
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content Section */}
      <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
        <CardHeader className="text-center border-b border-surface-muted/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1">
          <CardTitle className="text-2xl font-bold mb-2 font-hebrew">
                {activeTab === 'sessions' ? t('dashboard:course_card.course_lessons') : t('common:labels.exams')}
          </CardTitle>
          {isRTL ? (
            <MixedText className="text-surface-secondary max-w-2xl mx-auto font-hebrew">
                  {activeTab === 'sessions' 
                    ? (totalSessions === 0 ? t('dashboard:lessons.empty.description') : `${totalSessions} ${t('common:labels.sessions')}`)
                    : (totalExams === 0 ? t('exams:examList.emptyState.description') : `${totalExams} ${totalExams === 1 ? t('common:labels.exam') : t('common:labels.exams')}`)
              }
            </MixedText>
          ) : (
            <p className="text-surface-secondary max-w-2xl mx-auto font-hebrew">
                  {activeTab === 'sessions' 
                    ? (totalSessions === 0 ? t('dashboard:lessons.empty.description') : `${totalSessions} ${totalSessions !== 1 ? t('common:labels.sessions') : t('common:labels.session')}`)
                    : (totalExams === 0 ? t('exams:examList.emptyState.description') : `${totalExams} ${totalExams === 1 ? t('common:labels.exam') : t('common:labels.exams')}`)
              }
            </p>
          )}
            </div>
            {activeTab === 'exams' && refreshingStatuses && (
              <div className="flex items-center text-xs text-surface-secondary">
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                <span className="font-hebrew">
                  <MixedText>{t('common:messages.refreshing')}</MixedText>
                </span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {activeTab === 'sessions' ? (
            /* Sessions Grid */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto">
            {/* Add Session Card - Show first (left) for RTL */}
            {isRTL && (
              <SpaceCard 
                constellation="orion"
                onClick={() => navigate(`/quick/create-study-session?course_id=${courseId}`)}
                className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[180px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
              >
                <div className="p-4 md:p-8 flex flex-col items-center justify-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-3 md:mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                    <Plus className="w-5 h-5 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <CardTitle className="text-base md:text-xl mb-1 md:mb-2 font-hebrew text-surface-primary">
                    🚀 {t('sessions:create.title')}
                  </CardTitle>
                  <p className="text-surface-secondary text-xs md:text-sm max-w-xs font-hebrew">
                    {t('dashboard:course_card.start_new_study_session')}
                  </p>
                </div>
              </SpaceCard>
            )}

            {/* Existing Sessions */}
            {sessions.map((session) => {
              const totalAudio = session.transcriptions?.length || 0;
              const totalDocs = session.documents?.length || 0;
              const totalMaterials = session.study_materials?.length || 0;
              const totalQuestions = session.questions?.length || 0;
              
              return (
                <SpaceCard key={session.id} constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex flex-col">
                  <Link to={`/session/${session.id}`} className="block flex-1">
                    <CardHeader>
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        <div className={`hidden md:flex items-center justify-center w-14 h-14 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                          <Brain className="w-7 h-7 text-muted-purple" />
                        </div>
                        <div className={`flex-1 min-w-0 ${isRTL ? 'md:order-1 text-right md:pr-2' : 'md:order-2'}`}>
                          <div className={`flex items-start mb-2 ${isRTL ? 'justify-start flex-row-reverse' : 'justify-between'}`}>
                            <CardTitle className={`text-lg md:text-xl line-clamp-2 group-hover:text-vibrant-orange transition-colors duration-300 flex-1 ${isRTL ? 'text-right' : ''}`}>
                              {session.title}
                            </CardTitle>
                            <SessionStatusBadge 
                              status={getSessionStatus(session)} 
                              completionPercentage={calculateSessionProgress(session)}
                              size="sm"
                            />
                          </div>
                          
                          <div className={`flex items-center text-xs text-surface-muted ${isRTL ? 'justify-end' : ''}`}>
                            <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                              <span>{formatDate(session.created_at)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      {(session.ai_generated_description || session.description) ? (
                        <div className="mb-2 md:mb-4">
                          {isRTL ? (
                            <div className="text-right">
                              <p className="text-surface-primary text-sm leading-relaxed line-clamp-2 md:line-clamp-3">
                                {session.ai_generated_description || session.description}
                              </p>
                            </div>
                          ) : (
                            <p className="text-surface-primary text-sm leading-relaxed line-clamp-2 md:line-clamp-3">
                              {session.ai_generated_description || session.description}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mb-2 md:mb-4">
                            <p className={`text-surface-muted text-sm italic font-hebrew ${isRTL ? 'text-right' : ''}`}>
                                {totalAudio > 0 || totalDocs > 0 
                                  ? 'צור חומרי לימוד כדי לראות תיאור AI' 
                                  : t('dashboard:course_card.upload_content_to_get_started')}
                              </p>
                        </div>
                      )}
                      
                      <div className="hidden md:flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {totalAudio > 0 && (
                            <div className="flex items-center space-x-1 text-xs text-vibrant-orange">
                              <Mic className="w-3 h-3" />
                              <span className="font-hebrew">{totalAudio} {t('common:labels.audio')}</span>
                            </div>
                          )}
                          {totalDocs > 0 && (
                            <div className="flex items-center space-x-1 text-xs text-muted-purple">
                              <FileText className="w-3 h-3" />
                              <span className="font-hebrew">{totalDocs} {t('common:labels.docs')}</span>
                            </div>
                          )}
                          {totalMaterials > 0 && (
                            <div className="flex items-center space-x-1 text-xs text-vibrant-orange">
                              <Brain className="w-3 h-3" />
                              <span className="font-hebrew">{totalMaterials} {t('common:labels.materials')}</span>
                            </div>
                          )}
                          {totalQuestions > 0 && (
                            <div className="flex items-center space-x-1 text-xs text-muted-purple">
                              <HelpCircle className="w-3 h-3" />
                              <span className="font-hebrew">{totalQuestions} {t('common:labels.questions')}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="w-12 h-2 md:w-16 md:h-2 bg-surface-muted/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/70 transition-all duration-300"
                              style={{ width: `${calculateSessionProgress(session)}%` }}
                            />
                          </div>
                          <span className="text-xs text-surface-muted font-medium">
                            {calculateSessionProgress(session)}%
                          </span>
                        </div>
                      </div>

                      <div className="text-center mt-2 md:mt-4">
                        <div className="inline-flex items-center text-sm text-surface-secondary font-medium group-hover:text-vibrant-orange transition-colors duration-300 font-hebrew">
                          {isRTL ? (
                            <>
                              <span className="mr-2 transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
                              {t('dashboard:course_card.click_to_explore')}
                            </>
                          ) : (
                            <>
                              {t('dashboard:course_card.click_to_explore')}
                              <span className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Link>
                  
                  <CardFooter align="center" className="border-t border-surface-muted/20">
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditSession(session);
                        }}
                        className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                        aria-label={t('common:actions.edit_study_session')}
                      >
                        <Edit3 className="w-4 h-4 md:w-4 md:h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                        title={t('common:actions.delete_session')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardFooter>
                </SpaceCard>
              );
            })}

            {/* Add Session Card - Show last (right) for LTR */}
            {!isRTL && (
              <SpaceCard 
                constellation="ursa"
                onClick={() => navigate(`/quick/create-study-session?course_id=${courseId}`)}
                className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[180px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
              >
                <div className="p-4 md:p-8 flex flex-col items-center justify-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-3 md:mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                    <Plus className="w-5 h-5 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <CardTitle className="text-base md:text-xl mb-1 md:mb-2 font-hebrew text-surface-primary">
                    🚀 {t('sessions:create.title')}
                  </CardTitle>
                  <p className="text-surface-secondary text-xs md:text-sm max-w-xs font-hebrew">
                    {t('dashboard:course_card.start_new_study_session')}
                  </p>
                </div>
              </SpaceCard>
            )}
            </div>
          ) : (
            /* Exams Grid */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto">
              {/* Add Exam Card - Show first (left) for RTL */}
              {isRTL && (
                <SpaceCard
                  constellation="orion"
                  onClick={handleCreateExamClick}
                  className="cursor-pointer border-2 border-dashed border-muted-purple/30 hover:border-muted-purple/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[180px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
                >
                  <div className="p-4 md:p-8 flex flex-col items-center justify-center">
                    <div className="w-10 h-10 md:w-16 md:h-16 bg-muted-purple/10 rounded-full flex items-center justify-center mb-3 md:mb-4 group-hover:bg-muted-purple/20 transition-colors duration-300">
                      <Plus className="w-5 h-5 md:w-8 md:h-8 text-muted-purple group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <CardTitle className={`text-base md:text-xl mb-1 md:mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      🚀 <MixedText>{t('exams:examList.createExam')}</MixedText>
                    </CardTitle>
                    <p className={`text-surface-secondary text-xs md:text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examList.emptyState.description')}</MixedText>
                    </p>
                  </div>
                </SpaceCard>
              )}

              {/* Existing Exams */}
              {exams.map((exam) => (
                <SpaceCard
                  key={exam.id}
                  constellation="cassiopeia"
                  className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex flex-col"
                >
                  <Link
                    to={exam.processing_status === 'completed' ? `/course/${courseId}/exams/${exam.id}/practice` : '#'}
                    className={`block flex-1 ${exam.processing_status !== 'completed' ? 'cursor-default' : ''}`}
                  >
                    <CardHeader>
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        <div className={`hidden md:flex items-center justify-center w-14 h-14 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                          <GraduationCap className="w-7 h-7 text-muted-purple" />
                        </div>
                        <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-2' : 'order-2'}`}>
                          <div className={`flex items-start mb-2 ${isRTL ? 'justify-start flex-row-reverse' : 'justify-between'}`}>
                            <CardTitle className={`text-lg md:text-xl line-clamp-2 group-hover:text-vibrant-orange transition-colors duration-300 flex-1 ${isRTL ? 'text-right' : ''}`}>
                              <MixedText>{exam.title}</MixedText>
                            </CardTitle>
                            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                              {getStatusIcon(exam.processing_status)}
                              <span className={`text-sm font-medium font-hebrew mr-2 ml-2 ${isRTL ? 'text-right' : ''}`}>
                                <MixedText>{getStatusText(exam.processing_status)}</MixedText>
                              </span>
                            </div>
                          </div>
                          <div className={`flex items-center text-xs text-surface-secondary ${isRTL ? 'justify-end' : ''}`}>
                            <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                              <span className="font-hebrew">
                                <MixedText>{exam.exam_date ? formatDate(exam.exam_date) : formatDate(exam.created_at)}</MixedText>
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="mb-4">
                        <span className={`inline-block px-3 py-1 bg-muted-purple/10 text-muted-purple rounded-full text-sm font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                          <MixedText>{t(`exams:examList.examTypes.${exam.exam_type}`)}</MixedText>
                        </span>
                      </div>

                      <div className="text-center">
                        <div className="inline-flex items-center text-sm text-surface-secondary font-medium group-hover:text-vibrant-orange transition-colors duration-300 font-hebrew">
                          {exam.processing_status === 'completed' ? (
                            isRTL ? (
                              <>
                                <span className="mr-2 transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
                                <MixedText>{t('exams:examList.practice')}</MixedText>
                              </>
                            ) : (
                              <>
                                <MixedText>{t('exams:examList.practice')}</MixedText>
                                <span className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                              </>
                            )
                          ) : (
                            <MixedText>{exam.processing_status === 'processing' 
                              ? t('exams:examList.processing')
                              : exam.processing_status === 'failed'
                              ? t('exams:processing.retry')
                              : t('exams:processing.pending')
                            }</MixedText>
                          )}
                        </div>
          </div>
        </CardContent>
                  </Link>
                  
                  <CardFooter align="center" className="border-t border-surface-muted/20">
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditExam(exam);
                        }}
                        className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-blue/10 hover:text-vibrant-blue flex items-center justify-center"
                        title={t('common:actions.edit')}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteExam(exam.id, exam.title);
                        }}
                        disabled={deletingExam === exam.id}
                        className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                        title={t('exams:examList.delete')}
                      >
                        {deletingExam === exam.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </CardFooter>
                </SpaceCard>
              ))}

              {/* Add Exam Card - Show last (right) for LTR */}
              {!isRTL && (
                <SpaceCard
                  constellation="ursa"
                  onClick={handleCreateExamClick}
                  className="cursor-pointer border-2 border-dashed border-muted-purple/30 hover:border-muted-purple/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[180px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
                >
                  <div className="p-4 md:p-8 flex flex-col items-center justify-center">
                    <div className="w-10 h-10 md:w-16 md:h-16 bg-muted-purple/10 rounded-full flex items-center justify-center mb-3 md:mb-4 group-hover:bg-muted-purple/20 transition-colors duration-300">
                      <Plus className="w-5 h-5 md:w-8 md:h-8 text-muted-purple group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <CardTitle className={`text-base md:text-xl mb-1 md:mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      🚀 <MixedText>{t('exams:examList.createExam')}</MixedText>
                    </CardTitle>
                    <p className={`text-surface-secondary text-xs md:text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examList.emptyState.description')}</MixedText>
                    </p>
                  </div>
                </SpaceCard>
              )}
            </div>
          )}
        </CardContent>
      </SpaceCard>
    </div>

    {/* Edit Course Modal */}
    {editingCourse && (
      <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
        <Card className="w-full max-w-lg shadow-2xl mx-4">
          <CardHeader>
            <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
              ✨ {t('common:actions.edit_course')}
            </CardTitle>
          </CardHeader>

          <form onSubmit={handleUpdateCourse} className="space-y-5">
            <div>
              <label htmlFor="editCourseName" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  📚 {t('common:labels.course_name')} *
                </span>
              </label>
              <input
                type="text"
                id="editCourseName"
                value={editCourseName}
                onChange={(e) => setEditCourseName(e.target.value)}
                placeholder={t('common:placeholders.course_name_example')}
                className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew ${isRTL ? 'text-right' : ''}`}
                required
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <div>
              <label htmlFor="editCourseDescription" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  📝 {t('common:labels.description')} ({isRTL ? 'אופציונלי' : 'Optional'})
                </span>
              </label>
              <textarea
                id="editCourseDescription"
                value={editCourseDescription}
                onChange={(e) => setEditCourseDescription(e.target.value)}
                placeholder={t('common:placeholders.course_description')}
                className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 resize-none font-hebrew ${isRTL ? 'text-right' : ''}`}
                rows={3}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <CardFooter className="flex flex-col space-y-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={!editCourseName.trim()}
                className="w-full font-hebrew"
              >
                ✨ {t('common:buttons.update')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => {
                  setEditingCourse(false);
                  setEditCourseName('');
                  setEditCourseDescription('');
                }}
                className="w-full font-hebrew"
              >
                {t('common:buttons.cancel')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    )}

    {/* Edit Session Modal */}
    {editingSession && (
      <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
        <Card className="w-full max-w-lg shadow-2xl mx-4">
          <CardHeader>
            <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
              ✨ {t('common:actions.edit_study_session')}
            </CardTitle>
          </CardHeader>

          <form onSubmit={handleUpdateSession} className="space-y-5">
            <div>
              <CardContent>
                <div className="space-y-5">
                  <div>
                    <label 
                      htmlFor="sessionTitle" 
                      className={`block text-sm font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : ''}`}
                    >
                      {t('sessions:create.form.title')}
                    </label>
                    <input
                      id="sessionTitle"
                      type="text"
                      value={editSessionTitle}
                      onChange={(e) => setEditSessionTitle(e.target.value)}
                      className={`w-full px-4 py-3 bg-surface-dark border border-border-subtle rounded-lg text-surface-primary placeholder-surface-secondary focus:outline-none focus:ring-2 focus:ring-vibrant-orange focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                      placeholder={t('sessions:create.fields.title_placeholder')}
                      autoFocus
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>
              </CardContent>

              <CardFooter className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-3`}>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="flex-1"
                >
                  {t('common:buttons.update')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    setEditingSession(null);
                    setEditSessionTitle('');
                  }}
                  className="flex-1"
                >
                  {t('common:buttons.cancel')}
                </Button>
              </CardFooter>
            </div>
          </form>
        </Card>
      </div>
    )}

    {/* Edit Exam Modal */}
    {editingExam && (
      <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
        <Card className="w-full max-w-lg shadow-2xl mx-4">
          <CardHeader>
            <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
              {t('common:actions.edit')}
            </CardTitle>
          </CardHeader>

          <form onSubmit={handleUpdateExam} className="space-y-5">
            <div>
              <label htmlFor="editExamName" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  📄 {t('exams:examCreation.form.title')} *
                </span>
              </label>
              <input
                type="text"
                id="editExamName"
                value={editExamName}
                onChange={(e) => setEditExamName(e.target.value)}
                placeholder={t('exams:examCreation.form.titlePlaceholder')}
                className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew ${isRTL ? 'text-right' : ''}`}
                required
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <CardFooter className="flex flex-col space-y-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={!editExamName.trim()}
                className="w-full font-hebrew"
              >
                {t('common:buttons.update')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => {
                  setEditingExam(null);
                  setEditExamName('');
                }}
                className="w-full font-hebrew"
              >
                {t('common:buttons.cancel')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    )}

    {/* Exam Creation Method Modal */}
    {showCreationModal && (
      <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
        <Card className="w-full max-w-4xl shadow-2xl mx-auto">
          <CardHeader className="border-b border-surface-muted/20">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`${isRTL ? 'text-right' : ''}`}>
                <CardTitle className={`text-2xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  ✨ <MixedText>{t('exams:examCreation.chooseMethod.title')}</MixedText>
                </CardTitle>
                <CardContent className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('exams:examCreation.chooseMethod.subtitle')}</MixedText>
                </CardContent>
              </div>
              <button
                onClick={() => setShowCreationModal(false)}
                className="p-2 hover:bg-surface-muted/10 rounded-xl transition-colors duration-200"
              >
                <X className="w-6 h-6 text-surface-secondary" />
              </button>
            </div>
          </CardHeader>

          <CardContent>
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isRTL ? 'space-x-reverse' : ''}`}>
              {/* Generate with AI Card */}
              <SpaceCard
                constellation="orion"
                onClick={() => {
                  setShowCreationModal(false);
                  navigate(`/exams/ai-generate?course_id=${courseId}`);
                }}
                className="cursor-pointer border-2 border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-105 text-center"
              >
                <div className="p-6 md:p-8 flex flex-col items-center">
                  <div className="w-20 h-20 bg-vibrant-orange/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-vibrant-orange/20 transition-colors duration-300 relative">
                    <Brain className="w-10 h-10 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                    <Sparkles className="w-4 h-4 text-muted-purple absolute top-2 right-2 group-hover:scale-125 transition-transform duration-300" />
                  </div>
                  <CardTitle className={`text-xl font-bold mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    🛸 <MixedText>{t('exams:examCreation.chooseMethod.generateAI.title')}</MixedText>
                  </CardTitle>
                  <CardContent className={`text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:examCreation.chooseMethod.generateAI.description')}</MixedText>
                  </CardContent>
                  <div className="mt-6">
                    <span className={`inline-flex items-center px-4 py-2 bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 text-astronaut-white rounded-xl font-medium hover:from-vibrant-orange hover:to-vibrant-orange transition-all duration-300 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <Wand2 className="w-4 h-4" />
                      <span className="font-hebrew">
                        <MixedText>{t('exams:examCreation.chooseMethod.generateAI.button')}</MixedText>
                      </span>
                    </span>
                  </div>
                </div>
              </SpaceCard>

              {/* Upload File Card */}
              <SpaceCard
                constellation="cassiopeia"
                onClick={() => {
                  setShowCreationModal(false);
                  navigate(`/exams/create?course_id=${courseId}`);
                }}
                className="cursor-pointer border-2 border-muted-purple/30 hover:border-muted-purple/50 transition-all duration-300 hover:shadow-lg hover:scale-105 text-center"
              >
                <div className="p-6 md:p-8 flex flex-col items-center">
                  <div className="w-20 h-20 bg-muted-purple/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-muted-purple/20 transition-colors duration-300">
                    <Upload className="w-10 h-10 text-muted-purple group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <CardTitle className={`text-xl font-bold mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    📁 <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.title')}</MixedText>
                  </CardTitle>
                  <CardContent className={`text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.description')}</MixedText>
                  </CardContent>
                  <div className="mt-6">
                    <span className={`inline-flex items-center px-4 py-2 bg-gradient-to-r from-muted-purple to-muted-purple/80 text-astronaut-white rounded-xl font-medium hover:from-muted-purple hover:to-muted-purple transition-all duration-300 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <Upload className="w-4 h-4" />
                      <span className="font-hebrew">
                        <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.button')}</MixedText>
                      </span>
                    </span>
                  </div>
                </div>
              </SpaceCard>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
    </DashboardContainer>
  );
};

export default CourseView; 
